import React, { useMemo } from 'react';

import { Button, Flex } from '@opendoor/bricks/core';
import { Value } from '@opendoor/bricks/core/Select';

import { AnimationWrapper } from 'buying/components/AnimationWrapper';
import { FundingSource as FundingSourceEnum } from 'buying/components/buyDirectCheckout/commonTypes';
import { PurchaseInfo } from 'buying/components/buyDirectCheckout/PurchaseInfo';

interface IFundingSourceProps {
  closeDate?: string;
  downPaymentAmount?: number;
  downPaymentPercentage?: number;
  fundingSource?: FundingSourceEnum;
  listPrice: number;
  loanAmount?: number;
  fhaEligibilityDate?: Date;
  send: (args: string | { type: string; value?: string | boolean | number | Value }) => void;
}

/**
 * Funding Source Details
 * storybook-check-ignore
 */
export const FundingSource: React.FC<IFundingSourceProps> = ({
  closeDate,
  downPaymentAmount,
  downPaymentPercentage,
  fundingSource,
  listPrice,
  loanAmount,
  send,
  fhaEligibilityDate,
}) => {
  const handleForward = () => {
    send('FORWARD');
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    send({ type: 'setCloseDate', value: undefined });
    send('BACK');
    window.scrollTo(0, 0);
  };

  const isButtonDisabled = useMemo(() => {
    if (!closeDate) {
      return true;
    }

    // Cash, VA, and USDA loans don't require down payment info
    if (
      fundingSource &&
      [FundingSourceEnum.Cash, FundingSourceEnum.VA, FundingSourceEnum.USDA].includes(fundingSource)
    ) {
      return false;
    }

    return !downPaymentPercentage || !loanAmount || !downPaymentAmount;
  }, [closeDate, downPaymentAmount, downPaymentPercentage, fundingSource, loanAmount]);

  return (
    <AnimationWrapper>
      <Flex flexDirection="column" alignItems="flex-start" width="100%">
        <PurchaseInfo
          closeDate={closeDate}
          downPaymentAmount={downPaymentAmount}
          downPaymentPercentage={downPaymentPercentage}
          fundingSource={fundingSource}
          listPrice={listPrice}
          loanAmount={loanAmount}
          send={send}
          fhaEligibilityDate={fhaEligibilityDate}
        />
        <Flex
          flexDirection={['column', null, 'row-reverse']}
          alignItems="center"
          justifyContent="end"
          mt={8}
          width="100%"
        >
          <Button
            alignSelf={['center', null, 'flex-end']}
            analyticsName="buy-direct-checkout-share-pre-approval"
            aria-label="Share pre-approval"
            data-testid="buy-direct-checkout-share-pre-approval-button"
            disabled={isButtonDisabled}
            mt={8}
            onClick={handleForward}
            variant="primary"
            width={['100%', '100%', '100%', '35%']}
          >
            {fundingSource !== 'CASH' ? 'Share pre-approval' : 'Next'}
          </Button>
          <Button
            analyticsName="buy-direct-checkout-funding-source-back"
            aria-label="Back"
            data-testid="buy-direct-checkout-funding-source-back-button"
            width={['100%', '100%', '100%', '35%']}
            variant="secondary"
            onClick={handleBack}
            mt={8}
            mr={[null, null, 4]}
          >
            Back
          </Button>
        </Flex>
      </Flex>
    </AnimationWrapper>
  );
};
